// d:\pos_web\src\pages\stock-history\StockHistoryForm.js
import React, { useState, useEffect } from 'react';
import useApi from '../../hooks/useApi';
import InputField from '../../components/InputField';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';

const StockHistoryForm = ({
  // GenericListPage passes initialData as prop named singular of title
  // For "Stock Inventory History", this would be 'stock inventory history'
  // We'll alias it to initialData for clarity, though this form is primarily for adding.
  'stock inventory history': initialData,
  onClose,
  onSaveSuccess,
}) => {
  const api = useApi();
  const [formData, setFormData] = useState({
    barcode: '',
    quantity: '',
    type: 'purchase', // Default type
    storeId: '', // Added
    warehouseId: '', // Added
    remarks: '',
  });

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false); // For fetching dropdown data
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const [stores, setStores] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => {
    // This form is designed for adding new stock movements.
    // We are not implementing editing of existing history entries.
    // If initialData is passed (GenericListPage trying to "edit"),
    // we'll just reset to a blank "add" form.
    setFormData({
      barcode: '',
      quantity: initialData?.quantityChanged || '',
      type:  initialData?.type || 'purchase',
      storeId: initialData?.storeId?._id || initialData?.storeId || '',
      warehouseId: initialData?.warehouseId?._id || initialData?.warehouseId || '',
      remarks: initialData?.remarks || '',
    });
  }, [initialData]); // Effect runs if initialData changes, effectively resetting for "add"

  useEffect(() => {
    const fetchDropdownData = async () => {
      setPageLoading(true);
      try {
        const storesResponse = await api.get('stores', { queryParams: { limit: 1000, fields: 'name,_id,isActive', isActive: true } });
        setStores(storesResponse.data || storesResponse || []);

        // Assuming you have a 'warehouses' endpoint
        const warehousesResponse = await api.get('warehouses', { queryParams: { limit: 1000, fields: 'name,_id,isActive', isActive: true } });
        setWarehouses(warehousesResponse.data || warehousesResponse || []);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch dropdown data:", err);
        setError("Failed to load stores/warehouses. " + (err.message || ""));
      } finally {
        setPageLoading(false);
      }
    };
    fetchDropdownData();
  }, [api]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.barcode.trim()) errors.barcode = 'Barcode is required.';
    if (!formData.quantity.toString().trim()) errors.quantity = 'Quantity is required.';
    else if (isNaN(parseInt(formData.quantity))) errors.quantity = 'Quantity must be a number.';
    else if (parseInt(formData.quantity) === 0) errors.quantity = 'Quantity cannot be zero.';
    // if (!formData.storeId) errors.storeId = 'Store is required.'; // Make required if necessary
    // if (!formData.warehouseId) errors.warehouseId = 'Warehouse is required.'; // Make required if necessary
    
    // Quantity validation based on type (optional, backend should also validate)
    // if ((formData.type === 'purchase' || formData.type === 'sale_return' || formData.type === 'adjustment_in') && parseInt(formData.quantity) <= 0) {
    //   errors.quantity = 'Quantity must be positive for this type.';
    // } else if ((formData.type === 'adjustment_out' || formData.type === 'damage') && parseInt(formData.quantity) >= 0) {
    //   errors.quantity = 'Quantity must be negative for this type (or use positive and backend converts).';
    // }

    if (!formData.type) errors.type = 'Type is required.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    const payload = {
      barcode: formData.barcode,
      quantity: parseInt(formData.quantity), // Ensure quantity is an integer
      type: formData.type,
      storeId: formData.storeId || undefined,
      warehouseId: formData.warehouseId || undefined,
      remarks: formData.remarks || undefined, // Send undefined if empty
    };

    try {
      // This endpoint should handle the stock update and create a log entry.
      await api.post('inventory-log/add-by-barcode', payload); // Adjust endpoint if needed
      alert('Stock movement recorded successfully!');
      onSaveSuccess(); // Refreshes the list in GenericListPage
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Operation failed.");
      console.error("Stock movement form submission error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) return <LoadingSpinner />;

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4">
      {/* <h2 className="text-xl font-semibold mb-4">
        Record New Stock Movement
      </h2> */}
      {loading && <LoadingSpinner />}
      {/* This error state is for submission errors, pageLoading errors are handled by the return above or could be merged */}
      {error && <ErrorMessage message={error} />}

      <InputField label="Product Barcode" id="barcode" name="barcode" value={formData.barcode} onChange={handleChange} error={formErrors.barcode} required />
      <InputField label="Quantity" id="quantity" name="quantity" type="number" value={formData.quantity} onChange={handleChange} error={formErrors.quantity} required />
      
      <div>
        <label htmlFor="type" className="block text-sm font-medium text-gray-700">Type</label>
        <select id="type" name="type" value={formData.type} onChange={handleChange} required
          className={`mt-1 block w-full px-3 py-2 border ${formErrors.type ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}>
          <option value="purchase">Purchase (Stock In)</option>
          <option value="sale_return">Sale Return (Stock In)</option>
          <option value="adjustment_in">Manual Adjustment In (Stock In)</option>
          <option value="adjustment_out">Manual Adjustment Out (Stock Out)</option>
          <option value="damage">Damage (Stock Out)</option>
          {/* Add other types as needed. Sales are typically auto-generated. */}
        </select>
        {formErrors.type && <p className="mt-1 text-xs text-red-600">{formErrors.type}</p>}
      </div>

      <div>
        <label htmlFor="storeId" className="block text-sm font-medium text-gray-700">Store (Optional)</label>
        <select id="storeId" name="storeId" value={formData.storeId} onChange={handleChange}
          className={`mt-1 block w-full px-3 py-2 border ${formErrors.storeId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}>
          <option value="">Select a Store</option>
          {stores.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
        {formErrors.storeId && <p className="mt-1 text-xs text-red-600">{formErrors.storeId}</p>}
      </div>

      <div>
        <label htmlFor="warehouseId" className="block text-sm font-medium text-gray-700">Warehouse (Optional)</label>
        <select id="warehouseId" name="warehouseId" value={formData.warehouseId} onChange={handleChange}
          className={`mt-1 block w-full px-3 py-2 border ${formErrors.warehouseId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}>
          <option value="">Select a Warehouse</option>
          {warehouses.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
        </select>
        {formErrors.warehouseId && <p className="mt-1 text-xs text-red-600">{formErrors.warehouseId}</p>}
      </div>

      <InputField label="Remarks (Optional)" id="remarks" name="remarks" value={formData.remarks} onChange={handleChange} />

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button type="button" onClick={onClose} variant="secondary" disabled={loading}>Cancel</Button>
        <Button type="submit" variant="primary" isLoading={loading} disabled={loading}>Record Movement</Button>
      </div>
    </form>
  );
};

export default StockHistoryForm;